<template>
  <div ref="resizeArea" class="wrap">
    <div
      ref="settingArea"
      v-if="!isMapFullScreen"
      v-bind:style="{
        height: `${settingAreaHeight}px`,
        minHeight: `${settingAreaMinHeight}px`,
      }"
      class="settingArea"
    >
      <SimulationDeliveryTargetSetting
        :delivery-setting="deliverySetting"
        :readonly="readonly"
        @toggleDisplayState="onToggleDisplayState"
      />
    </div>
    <div
      ref="resizer"
      v-if="!isMapFullScreen"
      @dragend="resizeEnd"
      @drag="resize"
      v-bind:style="{ height: `${resizerHeight}px` }"
      class="resizer"
      draggable="true"
    />
    <div ref="mapArea" class="map">
      <AppOverlay v-if="isSelectedPoiLocationGroup">
        <AppSelectPoiLocationGroupMapModal />
      </AppOverlay>
      <MglMap
        :access-token="accessToken"
        :map-style="mapStyle"
        :center.sync="center"
        :zoom.sync="zoom"
        @load="onMapLoaded"
        @click="onMapClick"
        @dragend="filterLocations()"
        :keyboard="false"
      >
        <LayerWrapper
          v-if="currentSelectedGroup"
          :group="currentSelectedGroup"
          :locationsInFrame="locationsInFrame"
        />
        <MarkerWrapper
          v-for="location in locationsInFrame"
          v-bind:key="location.id"
          v-if="locationsInFrame.length < 100"
          :location="location"
          :selected-group="currentSelectedGroup"
          :delivery-setting="deliverySetting"
          :readonly="readonly"
        />
      </MglMap>
      <SimulationSearch
        :selected-group="currentSelectedGroup"
        v-if="isShowLocationSearch"
      />
      <v-btn
        v-if="!isMapFullScreen"
        @click="openFullScreenMap"
        icon
        fab
        small
        color="grey lighten-1"
        class="expandBtn"
      >
        <v-icon large color="white">mdi-fullscreen</v-icon>
      </v-btn>
      <v-btn
        v-else
        @click="closeFullScreenMap"
        icon
        fab
        small
        color="grey lighten-1"
        class="expandBtn"
      >
        <v-icon large color="white">mdi-fullscreen-exit</v-icon>
      </v-btn>
      <transition name="slide-up">
        <div
          v-if="locationsInFrame.length >= 100 && !isSelectedPoiLocationGroup"
          class="wrapper red--text"
        >
          地図の範囲内に地点数が100地点以上あるため、表示できません。
          ズームをして、表示エリアを狭めて下さい。
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { MglMap } from 'vue-mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import LayerWrapper from './SimulationContentDeliveryContentLayerWrapper'
import MarkerWrapper from './SimulationContentDeliveryContentMarkerWrapper'
import { SimulationLocation } from '@/models/Simulation/SimulationFormDelivery'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import { mapActions, mapGetters } from 'vuex'
import { minBy, maxBy } from 'lodash/math'
import SimulationSearch from '@/components/Shared/Simulation/SimulationContentDeliverySearch'
import SimulationDeliveryTargetSetting from '@/components/Shared/Simulation/SimulationContentDeliveryContentDeliveryTargetSetting'
import { modes } from '@/vuex/modules/simulation/constants'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'
import debounce from 'lodash/debounce'
import AppSelectPoiLocationGroupMapModal from '@/components/App/AppSelectPoiLoctionGroupMapModal'

export default {
  name: 'SimulationModelContentDelivery',
  components: {
    AppSelectPoiLocationGroupMapModal,
    MglMap,
    LayerWrapper,
    MarkerWrapper,
    SimulationSearch,
    SimulationDeliveryTargetSetting,
  },
  props: {
    form: {
      type: Object,
      default: null,
    },
    isSelectedPoiLocationGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accessToken:
        'pk.eyJ1Ijoid25vZ3VjaGkiLCJhIjoiY2lqY2c2dmxuMDAxZHUxamJwMGl3ZnV5ciJ9.WBilN5epB7RdyO-JbUQd2A',
      mapStyle: 'mapbox://styles/wnoguchi/ckbu3bvc303t41ip4yzmywi29',
      timer: null, // ダブルクリックとクリックを見分けるためのタイマー
      resizerHeight: 4, // リサイズ要素の高さ
      settingAreaMinHeight: 30, // 配信対象者設定の高さ最小値
      settingAreaHeightDefault: 188, // 配信対象者設定の高さ初期値
      settingAreaHeight: 188,
      isMapFullScreen: false,
      locationsInFrame: [],
    }
  },
  computed: {
    ...mapGetters('simulation', [
      'getDevState',
      'selectedLocation',
      'mapCenter',
      'mapZoom',
      'lastDragEndTime',
      'fitBounds',
      'selectedGroupId',
      'selectedGroupType',
      'lastSettingAreaHeight',
    ]),
    ...mapGetters('simulation', ['mode']),
    readonly() {
      return this.mode === modes.READONLY
    },
    zoom: {
      get() {
        return this.mapZoom
      },
      set(zoom) {
        this.setMapZoom(zoom)
      },
    },
    center: {
      get() {
        const { longitude, latitude } = this.mapCenter
        return [longitude, latitude]
      },
      set(center) {
        this.setMapCenter(center)
      },
    },
    deliverySetting() {
      return this.form.deliverySetting
    },
    simulationCustomLocationGroups() {
      return this.deliverySetting.menuSetting.simulationCustomLocationGroups
    },
    simulationPoiLocationGroups() {
      return this.deliverySetting.menuSetting.simulationPoiLocationGroups
    },
    currentSelectedGroup() {
      if (!this.selectedGroupId) return

      let currentSelectedGroup = null
      switch (this.selectedGroupType) {
        case locationGroupType.CUSTOM_LOCATION:
          currentSelectedGroup = this.simulationCustomLocationGroups.getGroup(
            this.selectedGroupId,
          )
          break
        case locationGroupType.POI_LOCATION:
          currentSelectedGroup = this.simulationPoiLocationGroups.getGroup(
            this.selectedGroupId,
          )
          break
      }

      if (!currentSelectedGroup) return null
      return currentSelectedGroup
    },
    locations() {
      if (!this.currentSelectedGroup || this.isSelectedPoiLocationGroup)
        return []
      return this.currentSelectedGroup.simulationLocations
    },
    isShowLocationSearch() {
      if (
        this.readonly ||
        this.selectedGroupType === locationGroupType.POI_LOCATION
      ) {
        return false
      }
      return !!this.currentSelectedGroup
    },
  },
  watch: {
    locations() {
      this.filterLocations()
    },
    zoom() {
      this.filterLocations()
    },
    selectedLocation(location) {
      if (!location.id || !this.map) {
        return
      }
      this.$nextTick(() => {
        this.flyTo(location)
      })
    },
    fitBounds(triggerFlag) {
      if (!triggerFlag) return
      if (this.locations.length === 0) return
      /*+ 地点が1つの場合は選択する*/
      if (this.locations.length === 1) {
        this.$nextTick(async () => {
          this.selectLocation(this.locations[0])
        })
        return
      }

      this.$nextTick(() => {
        const lngLats = this.locations.map(location => {
          const [lng, lat] = location.shape.getCoordinates()
          return {
            lng,
            lat,
          }
        })
        const maxLng = maxBy(lngLats, lngLat => lngLat.lng)
        const minLng = minBy(lngLats, lngLat => lngLat.lng)
        const maxLat = maxBy(lngLats, lngLat => lngLat.lat)
        const minLat = minBy(lngLats, lngLat => lngLat.lat)

        const padding = 50

        // 南西と東北の座標を指定して全ての地点が入る矩形を指定する
        this.map.fitBounds(
          [
            [minLng.lng, minLat.lat],
            [maxLng.lng, maxLat.lat],
          ],
          { padding },
        )
      })
    },
  },
  beforeDestroy() {
    // ページ遷移するタイミングで MglNavigationControl が残っているとエラーが発生するので強制削除
    // this.$refs.controls.$destroy()
  },
  created() {
    this.map = null
  },
  mounted() {
    /**
     * メニュー設定エリアの高さを復元する
     * */
    if (this.lastSettingAreaHeight) {
      this.settingAreaHeight = this.lastSettingAreaHeight
    }
  },
  methods: {
    ...mapActions('simulation', {
      selectLocation: 'SELECT_LOCATION',
      setMapCenter: 'SET_MAP_CENTER',
      setMapZoom: 'SET_MAP_ZOOM',
      setLastSettingAreaHeight: 'SET_LAST_SETTING_AREA_HEIGHT',
    }),
    async onMapLoaded(event) {
      this.map = event.map
      const language = new MapboxLanguage()
      this.map.setStyle(language.setLanguage(this.map.getStyle(), 'ja'))
      event.map.resize()
    },
    async onMapClick(event) {
      if (this.readonly) {
        return false
      }
      /**
       * 最終ドラッグから0.5秒以内のクリックイベントは無視する
       * ポップアップ内操作時にピンが立つのを防止する
       * */
      const lastDragEndTime = this.lastDragEndTime
      if (new Date().getTime() - lastDragEndTime <= 500) {
        return
      }

      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
        return
      }

      this.timer = setTimeout(async () => {
        this.timer = null
        if (this.selectedGroupId && this.currentSelectedGroup) {
          if (this.selectedGroupType === locationGroupType.POI_LOCATION) return

          const { lng, lat } = event.mapboxEvent.lngLat
          const location = await SimulationLocation.assignByLngLat(lng, lat)
          this.currentSelectedGroup.addLocation(location)
          this.selectLocation(location)
        } else {
          alert('地点グループを選択してください')
        }
      }, 300)
    },
    flyTo(location) {
      return new Promise(resolve => {
        if (this.map === undefined) return resolve()
        this.map.flyTo({ center: location.shape.getCoordinates(), zoom: 15 })
        this.map.once('moveend', () => {
          resolve()
        })
      })
    },
    resize(event) {
      if (event.pageY) {
        this.changeHeight(event.pageY)
      }
    },
    resizeEnd(event) {
      event.preventDefault()
      this.map.resize()
      /**
       * メニュー設定エリアの高さをVuexに保存する
       * mim-heightを下回る高さになる場合はmim-heightを保持
       */
      const height =
        this.settingAreaHeight <= this.settingAreaMinHeight
          ? this.settingAreaMinHeight
          : this.settingAreaHeight

      this.setLastSettingAreaHeight(height)
    },
    changeHeight(y) {
      const resizableArea = this.$refs.resizeArea
      const settingArea = this.$refs.settingArea
      const mapArea = this.$refs.mapArea

      // 設定エリア上端
      const settingAreaClientRect = settingArea.getBoundingClientRect()
      const settingAreaOffsetTop = settingAreaClientRect.top
      // リサイザーの高さ
      const resizeHeight = this.resizerHeight
      // 設定エリア上端+高さ
      const adjust = settingAreaOffsetTop + resizeHeight
      // 設定エリア高さ設定
      const settingAreaHeight = y - adjust
      this.settingAreaHeight = settingAreaHeight

      // 地図エリア高さ設定
      const mapAreaHeight =
        resizableArea.offsetHeight - settingAreaHeight - resizeHeight

      mapArea.style.height = `${mapAreaHeight}px`
    },
    onToggleDisplayState(isOpen) {
      if (isOpen) {
        // 配信対象者設定画面の高さを超える値が入れば良い
        // 地図＋設定画面の高さを代入すれば確実なのでこちらを利用する
        const resizedMapAreaHeight = 150
        this.settingAreaHeight =
          this.$refs.resizeArea.clientHeight - resizedMapAreaHeight
        return
      }
      this.initializeSettingAreaHeight()
      this.resizeMap()
    },
    openFullScreenMap() {
      this.isMapFullScreen = true
      this.resizeMap()
    },
    closeFullScreenMap() {
      this.isMapFullScreen = false
      this.initializeSettingAreaHeight()
      this.resizeMap()
    },
    initializeSettingAreaHeight() {
      const settingAreaMargin = 30
      this.settingAreaHeight = this.settingAreaHeightDefault + settingAreaMargin
    },
    resizeMap() {
      if (!this.map) {
        return
      }
      this.$nextTick(() => {
        this.map.resize()
      })
    },
    filterLocations: debounce(function() {
      if (!this.map) return
      const frame = this.map.getBounds()
      this.locationsInFrame = this.locations.filter(point => {
        // 表示域内の地点をフィルター
        return (
          (point.shape.coordinates[0] - frame._ne.lng) *
            (point.shape.coordinates[0] - frame._sw.lng) <=
            0 &&
          (point.shape.coordinates[1] - frame._ne.lat) *
            (point.shape.coordinates[1] - frame._sw.lat) <=
            0
        )
      })
    }),
  },
}
</script>

<style scoped>
.wrap {
  display: flex;
  flex-direction: column;
}
.settingArea {
  overflow: scroll;
}
.map {
  flex: 1;
  min-height: 30px;
  position: relative;
}

.resizer {
  background-color: #a0a0a0;
  cursor: row-resize;
}

.expandBtn {
  position: absolute;
  top: 0;
  left: 0;
}

.wrapper {
  position: absolute;
  bottom: 0;
  width: 100%;
  background: #cce5ff;
  padding: 10px 20px;
  z-index: 100;
}
</style>
